import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthProvider";
import { Link, useNavigate } from "react-router-dom";
import axios from "../api/axios";
import { Helmet } from "react-helmet-async";
import './styles/LoginSignup.css';

const Login = () => {
  const { login, auth, loading: authLoading } = useAuth();
  const [formData, setFormData] = useState({ identifier: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);

  const [twoFARequired, setTwoFARequired] = useState(false);
  const [twoFAMethod, setTwoFAMethod] = useState(null);
  const [userId, setUserId] = useState(null);
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const otp = otpDigits.join("");
  const [usingBackupCode, setUsingBackupCode] = useState(false);
  const [backupCode, setBackupCode] = useState("");
  const [error, setError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [verifyingOTP, setVerifyingOTP] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (auth.isAuthenticated) {
      if (["admin", "siteadmin"].includes(auth.user?.role)) {
        navigate("/admin/dashboard");
      } else {
        navigate("/");
      }
    }
  }, [auth, navigate]);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleOtpChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;
    const updatedDigits = [...otpDigits];
    updatedDigits[index] = value;
    setOtpDigits(updatedDigits);
    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`).focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      const updatedDigits = [...otpDigits];
      updatedDigits[index - 1] = "";
      setOtpDigits(updatedDigits);
      document.getElementById(`otp-${index - 1}`).focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasteData.length === 6) {
      setOtpDigits(pasteData.split(''));
      document.getElementById('otp-5').focus();
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoginLoading(true);
    try {
      const response = await login(formData);
      if (response?.twofactorRequired) {
        setUserId(response.userId);
        setTwoFARequired(true);
        setTwoFAMethod(response.twoFactorMethod);
      } else if (response?.success || response?.user) {
        // AuthProvider handles redirect
      } else {
        setError(response?.error || "Login failed.");
      }
    } catch (err) {
      setError("Login failed. Please try again.");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setVerifyingOTP(true);
    setError("");
    try {
      const payload = usingBackupCode
        ? { userId, backupCode }
        : { userId, otp };

      const res = await axios.post('/api/auth/verify-login-2fa', payload, { withCredentials: true });
      if (res.data?.user) {
        await axios.get('/api/auth/me');
        if (["admin", "siteadmin"].includes(res.data.user.role)) {
          window.location.href = "/admin/dashboard";
        } else {
          window.location.href = "/user/profile";
        }
      } else {
        setError(res.data?.error || "Verification failed.");
      }
    } catch (err) {
      setError("Verification failed. Please try again.");
    } finally {
      setVerifyingOTP(false);
    }
  };

  if (authLoading) {
    return (
      <div className="auth-page-wrapper">
        <div className="auth-container">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <>
        <Helmet>
          <title>Admin - Website Name</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        
      <div className="auth-page-wrapper">
        <div className="auth-container">
          <h2>Login</h2>
          {error && <p className="error">{error}</p>}
          {!twoFARequired ? (
            <form onSubmit={handleLogin}>
              <label htmlFor="login-identifier" className="sr-only">Username or Email</label>
              <input
                id="login-identifier"
                type="text"
                name="identifier"
                placeholder="Username or Email"
                onChange={handleChange}
                value={formData.identifier}
                required
                autoFocus
              />
              <div className="password-wrapper">
                <label htmlFor="login-password" className="sr-only">Password</label>
                <input
                  id="login-password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Password"
                  onChange={handleChange}
                  value={formData.password}
                  required
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  <i className={`fa-solid ${showPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
                </button>
              </div>
              <button type="submit" disabled={loginLoading}>
                {loginLoading ? <div className="spinner"></div> : "Login"}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP}>
              <p className="info">
                {usingBackupCode
                  ? "Enter a backup code."
                  : `Enter the 6-digit code from your ${twoFAMethod === "email" ? "email" : "authenticator app"}.`}
              </p>
              {!usingBackupCode ? (
                <div className="otp-container" onPaste={handleOtpPaste}>
                  <div className="otp-boxes-wrapper">
                    {otpDigits.map((digit, index) => (
                      <input
                        key={index}
                        id={`otp-${index}`}
                        type="text"
                        inputMode="numeric"
                        maxLength="1"
                        value={digit}
                        onChange={(e) => handleOtpChange(e.target.value, index)}
                        onKeyDown={(e) => handleOtpKeyDown(e, index)}
                        className="otp-box"
                        aria-label={`Enter OTP digit ${index + 1} of 6`}
                        placeholder=""
                      />
                    ))}
                  </div>
                  <button
                    type="button"
                    className="switch-method-button"
                    onClick={() => {
                      setUsingBackupCode(true);
                      setOtpDigits(["", "", "", "", "", ""]);
                    }}
                    aria-label="Use backup code"
                  >
                    Use Backup Code Instead
                  </button>
                </div>
              ) : (
                <>
                  <label htmlFor="backup-code-input" className="sr-only">Enter backup code</label>
                  <input
                    id="backup-code-input"
                    type="text"
                    placeholder="Enter your backup code"
                    value={backupCode}
                    onChange={(e) => setBackupCode(e.target.value)}
                    className="backup-code-input"
                    required
                  />
                  <button
                    type="button"
                    className="switch-method-button"
                    onClick={() => {
                      setUsingBackupCode(false);
                      setBackupCode("");
                    }}
                    aria-label="Use 6-digit OTP"
                  >
                    Use 6-Digit Code Instead
                  </button>
                </>
              )}
              <button
                type="submit"
                disabled={
                  verifyingOTP ||
                  (usingBackupCode ? backupCode.length < 8 : otp.length !== 6)
                }
                aria-label={`Verify ${usingBackupCode ? "Backup Code" : "OTP Code"}`}
              >
                {verifyingOTP ? <div className="spinner"></div> : `Verify ${usingBackupCode ? "Backup Code" : "OTP"}`}
              </button>
            </form>
          )}
          <p>
            <Link to="/forgot-password">Forgot Password?</Link>
          </p>
        </div>
      </div>
    </>
  );
};

export default Login;